// SB6-ANA-FILT-06 — info-affordance copy for the Global Data Point Filter.
//
// All copy is authored in **Markdown** (D11) and rendered through
// `react-markdown` (sanitised — no raw HTML) by the per-level tooltips
// (`FilterLevel.tsx`, `KindLevel.tsx`) and the general info popover
// (`FilterWidget.tsx`). The voice is deliberately a Senior QA /
// ISO-manager register: a short heading, then a precise definition and
// (where it helps) a bulleted enumeration, so an auditor reading a level
// has no open question about what it filters or why.
//
// Level restructure (D3): the L4 "Defect Type" tree level was removed in
// 7.2.0; defect-type narrowing now lives in the Defect Filter Widget. The
// ladder is six levels — the WIRE axis keys are unchanged (`component`,
// `location`, `severity` still carry those names on the wire); only the
// human-facing L-number moved up by one.

/**
 * General popover copy. Renders Markdown. Explains the cascade contract
 * (left-to-right narrowing, per-level live counts, bypass semantics) in
 * full so the analyst understands the model before touching a control.
 */
export const FILTER_GENERAL_INFO = `### Global Data Point Filter

This filter defines the **shared set of data points** that every widget on
this page operates on. A *data point* is a single inspection position
(one image), and it is the unit every count and collection reduces to.

**How it works**

- The six levels apply **left to right**. Each level narrows the set
  produced by the levels to its left (a logical **AND** between levels).
- Every level shows a **live count** per option, recomputed against the
  choices already made to its left.
- A level you leave untouched is **bypassed** — it imposes no
  restriction, and every data point passes through it.
- Within a single level, multiple selections combine as **OR** (any
  selected option qualifies).

**Defaults & persistence**

- The filter opens on **Active** data points only; it never changes on
  its own. *Reset to default* restores that baseline.
- Your selection is saved to your profile and restored on your next
  visit, and is also encoded in the page URL so a view can be shared.`;

// Per-level tooltips, keyed by the FacetAxis.axis value. Markdown.
export const FILTER_LEVEL_INFO: Record<string, string> = {
  status: `**Status**

Separates data points by lifecycle state.

- **Active** — in scope for review.
- **Inactive** — excluded from review, typically because:
  - the position overlaps another data point that already has adequate
    coverage,
  - the image carries no relevant information, or
  - the pilot flagged it inactive during acquisition.

Show **Inactive** when you need to inspect or correct those exclusions.`,
  reviewState: `**Review**

Filters by **your** review progress on each data point.

- **Seen** — you have already opened and reviewed it.
- **Unseen** — still awaiting your review.

Use this to resume exactly where you left off without re-checking work
already done.`,
  kind: `**Kind**

Filters by the type of evidence attached to a data point.

- **AI** — data points carrying machine-learning findings.
- **Defect** — data points carrying confirmed, human-verified defects.

Selecting both returns data points that have **either** kind (AI *or*
Defect *or* both). Use the **sliders icon** on a button to open that
kind's advanced filter and refine which findings or defects qualify.`,
  component: `**Component**

The turbine component a data point belongs to (for example, rotor blade
*RB1*). Component types use these abbreviations:

- **T** — Tower
- **F** — Foundation
- **N** — Nacelle
- **SPI** — Spinner
- **RB** — Rotor Blade
- **ACC** — Accessories

Hover a component option to see its full type name and serial number.`,
  location: `**Location**

The location detail on the selected component — for example **LE**
(Leading Edge) or **TE** (Trailing Edge).

Hover a location option to see its full name.`,
  severity: `**Severity**

The defect severity grade assigned from the data set's active defect
class model.

- Available only when a class model is configured. With a real model,
  every defect carries a grade.
- When the class model is **None**, defects have no grade and this level
  imposes no restriction.`,
};

// Per-level display titles + their L-number prefix. SB6-ANA-FILT-06 (D3):
// the L4 Defect Type level was dropped; Component→L4, Location→L5,
// Severity→L6. Wire axis keys are unchanged.
export const FILTER_LEVEL_TITLES: Record<
  string,
  { prefix: string; label: string }
> = {
  status: { prefix: "L1", label: "Status" },
  reviewState: { prefix: "L2", label: "Review" },
  kind: { prefix: "L3", label: "Kind" },
  component: { prefix: "L4", label: "Component" },
  location: { prefix: "L5", label: "Location" },
  severity: { prefix: "L6", label: "Severity" },
};

// Bypass hint copy per level (shown when the level has no selection).
// SB6-ANA-FILT-06 (D12): L3 → "No kind filter"; L6 Severity when the
// class model is None → "No defect class model".
export const FILTER_LEVEL_BYPASS: Record<string, string> = {
  status: "All statuses",
  reviewState: "All Review States",
  kind: "No kind filter",
  component: "All Components",
  location: "All Locations",
  severity: "All grades",
};

// SB6-ANA-FILT-06 (D12) — Severity bypass text when the active defect
// class model is None (no grades exist). Used in place of the default
// "All grades" so the analyst understands there is nothing to grade by.
export const SEVERITY_NO_CLASS_MODEL_BYPASS = "No defect class model";
